// Combat Alpine.js component

import { combatService } from './combat.service.js';

export const combat = () => ({
  // State
  availableEnemies: [],
  currentCombat: null,
  loading: false,
  error: null,
  selectedEnemyId: null,
  combatStats: { total: 0, victories: 0, defeats: 0, fled: 0, winRate: '0.0' },
  
  // Initialize component
  init() {
    console.log('Combat component initialized');
    this.loadEnemies();
    this.loadCombatStats();
    
    // Load current combat if exists
    this.currentCombat = combatService.getCurrentCombat();
    
    // Listen for combat state changes
    window.addEventListener('combat:state-changed', () => {
      this.currentCombat = combatService.getCurrentCombat();
      this.loadCombatStats();
    });
    
    // Auto-update combat UI every second during active combat
    this.startCombatTimer();
  },
  
  // Load available enemies for current location
  loadEnemies() {
    this.loading = true;
    this.error = null;
    
    try {
      const currentLocation = this.$store.app.p().location;
      this.availableEnemies = combatService.getEnemiesForLocation(currentLocation);
      console.log(`Loaded ${this.availableEnemies.length} enemies for ${currentLocation}`);
    } catch (error) {
      console.error('Failed to load enemies:', error);
      this.error = 'Error al cargar enemigos';
    } finally {
      this.loading = false;
    }
  },
  
  // Load combat statistics
  loadCombatStats() {
    this.combatStats = combatService.getCombatStats();
  },
  
  // Start combat with selected enemy
  async startCombat(enemyId) {
    if (this.loading) return;
    
    try {
      this.loading = true;
      this.error = null;
      
      const currentLocation = this.$store.app.p().location;
      this.currentCombat = combatService.startCombat(enemyId, currentLocation);
      
      this.$store.app.addNotification(
        `¡Combate iniciado contra ${this.currentCombat.enemy.name}!`,
        'info'
      );
      
      console.log('Combat started:', this.currentCombat);
      
    } catch (error) {
      console.error('Failed to start combat:', error);
      this.error = error.message;
      this.$store.app.addNotification('Error al iniciar combate', 'error');
    } finally {
      this.loading = false;
    }
  },
  
  // Player attacks
  async playerAttack() {
    if (this.loading || !this.currentCombat || this.currentCombat.turn !== 'player') return;
    
    try {
      this.loading = true;
      
      this.currentCombat = await combatService.playerAttack();
      
      // If combat is still active and it's enemy turn, auto-attack after delay
      if (this.currentCombat.status === 'active' && this.currentCombat.turn === 'enemy') {
        setTimeout(() => {
          this.enemyAttack();
        }, 1500); // 1.5 second delay for enemy response
      }
      
    } catch (error) {
      console.error('Player attack failed:', error);
      this.error = error.message;
    } finally {
      this.loading = false;
    }
  },
  
  // Enemy attacks (auto-triggered)
  async enemyAttack() {
    if (!this.currentCombat || this.currentCombat.turn !== 'enemy') return;
    
    try {
      this.currentCombat = await combatService.enemyAttack();
      
    } catch (error) {
      console.error('Enemy attack failed:', error);
      this.error = error.message;
    }
  },
  
  // Player flees
  async fleeCombat() {
    if (this.loading || !this.currentCombat) return;
    
    try {
      this.loading = true;
      
      const result = await combatService.fleeCombat();
      
      if (result.success) {
        this.$store.app.addNotification('Has huido del combate', 'warning');
        this.currentCombat = null;
      } else {
        this.$store.app.addNotification('No pudiste escapar!', 'error');
        this.currentCombat = result.combat;
        
        // Enemy gets free attack
        setTimeout(() => {
          this.enemyAttack();
        }, 1000);
      }
      
    } catch (error) {
      console.error('Flee failed:', error);
      this.error = error.message;
    } finally {
      this.loading = false;
    }
  },
  
  // Claim rewards and end combat
  claimRewards() {
    if (!this.currentCombat || !this.currentCombat.rewards) return;
    
    const rewards = this.currentCombat.rewards;
    
    // Add coins to player
    this.$store.app.updatePlayer({
      money: this.$store.app.p().money + rewards.coins
    });
    
    // TODO: Add items to inventory
    // TODO: Add experience to player
    
    this.$store.app.addNotification(
      `Recompensas reclamadas: ${rewards.coins} monedas`,
      'success'
    );
    
    this.currentCombat = null;
  },
  
  // Get enemy rarity class
  getEnemyRarityClass(enemy) {
    const baseClass = 'enemy-card';
    return `${baseClass} rarity-${enemy.rarity}`;
  },
  
  // Get health percentage
  getHealthPercentage(current, max) {
    return Math.max(0, (current / max) * 100);
  },
  
  // Get health bar color
  getHealthBarColor(current, max) {
    const percentage = this.getHealthPercentage(current, max);
    if (percentage > 60) return '#10b981'; // Green
    if (percentage > 30) return '#f59e0b'; // Yellow
    return '#ef4444'; // Red
  },
  
  // Check if player can act
  canPlayerAct() {
    return this.currentCombat && 
           this.currentCombat.status === 'active' && 
           this.currentCombat.turn === 'player' && 
           !this.loading;
  },
  
  // Check if combat is active
  isCombatActive() {
    return this.currentCombat && this.currentCombat.status === 'active';
  },
  
  // Check if combat is finished
  isCombatFinished() {
    return this.currentCombat && this.currentCombat.status !== 'active';
  },
  
  // Get latest combat log entries (last 5)
  getRecentCombatLog() {
    if (!this.currentCombat || !this.currentCombat.log) return [];
    return this.currentCombat.log.slice(-5);
  },
  
  // Get combat log message class
  getLogMessageClass(logEntry) {
    const baseClass = 'log-message';
    return `${baseClass} log-${logEntry.type}`;
  },
  
  // Format combat duration
  formatCombatDuration() {
    if (!this.currentCombat || !this.currentCombat.startTime) return '';
    
    const start = new Date(this.currentCombat.startTime);
    const end = this.currentCombat.endTime ? new Date(this.currentCombat.endTime) : new Date();
    const duration = Math.floor((end - start) / 1000);
    
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  },
  
  // Start combat timer for UI updates
  startCombatTimer() {
    setInterval(() => {
      if (this.currentCombat && this.currentCombat.status === 'active') {
        // Force reactivity update
        this.$nextTick();
      }
    }, 1000);
  },
  
  // Get combat result message
  getCombatResultMessage() {
    if (!this.currentCombat) return '';
    
    switch (this.currentCombat.status) {
      case 'victory':
        return '¡Victoria! Has derrotado al enemigo';
      case 'defeat':
        return 'Has sido derrotado...';
      case 'fled':
        return 'Has huido del combate';
      default:
        return '';
    }
  },
  
  // Get combat result class
  getCombatResultClass() {
    if (!this.currentCombat) return '';
    
    switch (this.currentCombat.status) {
      case 'victory':
        return 'result-victory';
      case 'defeat':
        return 'result-defeat';
      case 'fled':
        return 'result-fled';
      default:
        return '';
    }
  },
  
  // Refresh enemies list
  refresh() {
    this.loadEnemies();
    this.loadCombatStats();
  },
  
  // Cleanup when component is destroyed
  destroy() {
    this.currentCombat = null;
    console.log('Combat component destroyed');
  }
});